import * as yup from 'yup';

import { EAssessmentDecision, EExclusionReason, EReviewingStage, TAssessmentInput } from '../../types';
import { EXCLUSION_REASON_ENABLED } from '../../constants';

const isExcluded = (decision: unknown): boolean =>
  Array.isArray(decision) && decision[0] === EAssessmentDecision.EXCLUDED;
const isOtherReason = (reason: unknown): boolean => Array.isArray(reason) && reason[0] === EExclusionReason.OTHER;

// BE mirrors the decision/exclusion/comment cross-rules in assessments.service.ts validateDecisionFields.
export const bulkUploadFormSchema = yup
  .object({
    products: yup.array().of(yup.string().required()).required(),
    decision: yup
      .array()
      .of(yup.string().required())
      .when('products', {
        is: (p: unknown) => Array.isArray(p) && p.length > 0,
        then: (s) => s.min(1, 'Decision is required.').required(),
        otherwise: (s) => s.notRequired(),
      }),
    reviewingStage: yup.array().of(yup.string().defined()).notRequired(),
    exclusionReason: yup
      .array()
      .of(yup.string().required())
      .when('decision', {
        is: (d: unknown) => EXCLUSION_REASON_ENABLED && isExcluded(d),
        then: (s) => s.min(1, 'Exclusion reason is required.').required(),
        otherwise: (s) => s.notRequired(),
      }),
    comment: yup
      .string()
      .max(5000)
      .when(['decision', 'exclusionReason'], {
        is: (d: unknown, r: unknown) => EXCLUSION_REASON_ENABLED && isExcluded(d) && isOtherReason(r),
        then: (s) => s.trim().min(1, 'Please describe the reason for exclusion.').required(),
        otherwise: (s) => s.notRequired(),
      }),
  })
  .required();

export type TBulkUploadFormData = yup.InferType<typeof bulkUploadFormSchema>;

export const bulkFormToAssessment = (data: TBulkUploadFormData): TAssessmentInput | undefined => {
  const products = Array.from(new Set(data.products?.filter((p): p is string => Boolean(p)) ?? []));
  if (products.length === 0) return undefined;
  const decision = data.decision?.[0] as EAssessmentDecision;
  if (!decision) return undefined;
  const stage = data.reviewingStage?.[0] || undefined;
  const reviewingStage = stage as EReviewingStage | undefined;
  const exclusionReason =
    decision === EAssessmentDecision.EXCLUDED ? (data.exclusionReason?.[0] as EExclusionReason | undefined) : undefined;
  const comment = data.comment || undefined;
  return { products, decision, reviewingStage, exclusionReason, comment };
};
